import { useState, useCallback } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import ListAltIcon from '@material-ui/icons/ListAlt';

export interface EnvVariable {
  key: string;
  defaultValue: string;
}

export interface ProjectDependencies {
  project: {
    id: number;
    name: string;
    path_with_namespace: string;
  };
  branch: string;
  envFound: boolean;
  envVars: EnvVariable[];
  suggestions: Record<string, string>;
  relatedResources: Record<string, string[]>;
}

export interface LoadDependenciesResult {
  project: {
    id: number;
    name: string;
    path_with_namespace: string;
    description: string | null;
    default_branch: string;
    web_url: string;
  };
  branch: string;
  envVars: EnvVariable[];
  suggestions: Record<string, string>;
  suggestionGroups: Record<string, string>;
  relatedResources: Record<string, string[]>;
}

export interface LoadGroupDependenciesResult {
  group: string;
  projects: ProjectDependencies[];
}

export interface LoadDependenciesButtonProps {
  projectPath: string;
  branch: string;
  mode: 'project' | 'group';
  disabled?: boolean;
  onLoaded: (result: LoadDependenciesResult) => void;
  onGroupLoaded: (result: LoadGroupDependenciesResult) => void;
  onError: (message: string) => void;
}

export function LoadDependenciesButton({ projectPath, branch, mode, disabled, onLoaded, onGroupLoaded, onError }: LoadDependenciesButtonProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);

  const handleLoad = useCallback(async () => {
    if (!projectPath.trim()) return;

    setLoading(true);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('gitlab-catalog-sync');

      if (mode === 'group') {
        // Group mode: fetch dependencies for all projects in the group
        const params = new URLSearchParams({ group: projectPath.trim() });
        if (branch.trim()) {
          params.set('ref', branch.trim());
        }

        const res = await fetchApi.fetch(`${baseUrl}/group-env?${params.toString()}`);

        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(body.error || `HTTP ${res.status}`);
        }

        const data = await res.json();
        const projects: ProjectDependencies[] = (data.projects ?? []).map((p: any) => ({
          project: p.project,
          branch: p.branch,
          envFound: p.envFound,
          envVars: p.dependencies ?? [],
          suggestions: p.suggestions ?? {},
          relatedResources: p.relatedResources ?? {},
        }));

        // Filter only projects that have dependencies detected
        const withDeps = projects.filter(p => p.envVars.length > 0);

        if (withDeps.length === 0) {
          onError(`Nenhuma dependência encontrada nos projetos do grupo "${projectPath.trim()}"`);
          return;
        }

        onGroupLoaded({ group: data.group, projects: withDeps });
      } else {
        // Single project mode
        const params = new URLSearchParams({ project: projectPath.trim() });
        if (branch.trim()) {
          params.set('ref', branch.trim());
        }

        const res = await fetchApi.fetch(`${baseUrl}/repo-env?${params.toString()}`);

        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(body.error || `HTTP ${res.status}`);
        }

        const data = await res.json();

        if (!data.envExample.found) {
          onError(`Arquivo .env.example não encontrado na branch "${data.branch}"`);
          return;
        }

        const envVars: EnvVariable[] = data.dependencies ?? [];

        if (envVars.length === 0) {
          onError(`Nenhuma variável de dependência encontrada no .env.example (keywords: ${(data.keywords ?? []).join(', ')})`);
          return;
        }

        onLoaded({
          project: data.project,
          branch: data.branch,
          envVars,
          suggestions: data.suggestions ?? {},
          suggestionGroups: data.suggestionGroups ?? {},
          relatedResources: data.relatedResources ?? {},
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      onError(message);
    } finally {
      setLoading(false);
    }
  }, [projectPath, branch, mode, discoveryApi, fetchApi, onLoaded, onGroupLoaded, onError]);

  return (
    <Button
      variant="outlined"
      color="default"
      startIcon={loading ? <CircularProgress size={18} /> : <ListAltIcon />}
      onClick={handleLoad}
      disabled={disabled || loading || !projectPath.trim()}
    >
      {loading ? 'Carregando...' : 'Carregar Dependências'}
    </Button>
  );
}
